/* eslint-disable react-refresh/only-export-components -- provider co-locates its
   hook + context export, matching the other context modules in this dir. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import apiService from '../services/apiService.js';
import { useAuth } from './Auth0Context.jsx';
import { DEFAULT_CARD_DECK } from '../components/combat/cards/combatCardArt.js';

const PlayerPreferencesContext = createContext(null);

export function PlayerPreferencesProvider({ children }) {
  const { user, getAccessTokenSilently } = useAuth();
  const tokenReady = Boolean(user) && typeof getAccessTokenSilently === 'function';
  const [prefs, setPrefs] = useState(null);
  // BOU-764: available decks now carry a per-card URL map from the backend.
  // Cached here so combat surfaces can render deck-skinned faces without
  // constructing /assets/combat-cards/decks/... paths into the static mount.
  const [availableDecks, setAvailableDecks] = useState(null);

  // Make sure apiService has a token provider before we hit the API. App.jsx
  // wires this for /:sessionId/dm but combat / player routes mount different
  // shells, so we register here too — multiple registrations are idempotent.
  useEffect(() => {
    if (!tokenReady || apiService.getAccessToken) return;
    apiService.setTokenProvider(async () => {
      try {
        return await getAccessTokenSilently();
      } catch {
        return null;
      }
    });
  }, [tokenReady, getAccessTokenSilently]);

  const refresh = useCallback(async () => {
    try {
      const [data, decks] = await Promise.all([
        apiService.getPlayerPreferences(),
        apiService.getAvailableCardDecks().catch(() => null),
      ]);
      setPrefs(data);
      if (decks) {
        setAvailableDecks(decks);
      }
      return data;
    } catch (err) {
      console.error('Failed to load player preferences:', err);
      return null;
    }
  }, []);

  // BOU-1775: re-fetch ONLY the deck map (cheaper than full refresh) to keep
  // gated-deck signed card URLs fresh without clobbering locally-applied prefs.
  const refreshDecks = useCallback(async () => {
    try {
      const decks = await apiService.getAvailableCardDecks();
      if (decks) setAvailableDecks(decks);
    } catch {
      // Keep the last-known deck map; a transient failure must not blank cards.
    }
  }, []);

  const applyPreferences = useCallback((nextPrefs) => {
    if (!nextPrefs || typeof nextPrefs !== 'object') return;
    setPrefs((prev) => ({
      ...(prev || {}),
      ...nextPrefs,
    }));
  }, []);

  useEffect(() => {
    if (!tokenReady) return;
    // Wait a tick so the token provider above has been registered.
    let cancelled = false;
    const tryRefresh = async (attempt = 0) => {
      if (cancelled) return;
      if (!apiService.getAccessToken && attempt < 20) {
        setTimeout(() => tryRefresh(attempt + 1), 50);
        return;
      }
      await refresh();
    };
    tryRefresh();
    return () => { cancelled = true; };
  }, [tokenReady, refresh]);

  // BOU-1775: gated (premium) deck card art loads as <img src> via short-lived
  // signed URLs, but the deck map is fetched once per app session and cached.
  // In a long-lived or backgrounded tab the signatures expire, so the art 401s
  // ("Authentication required for this deck") and cards render black until a
  // full reload. Re-fetch the deck map when the tab returns to the foreground
  // and on a steady interval so the signed URLs stay valid in-session.
  useEffect(() => {
    if (!tokenReady) return undefined;
    const REFRESH_INTERVAL_MS = 30 * 60 * 1000;
    const handleVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refreshDecks();
      }
    };
    const interval = setInterval(refreshDecks, REFRESH_INTERVAL_MS);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }
    return () => {
      clearInterval(interval);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [tokenReady, refreshDecks]);

  const cardDeck = prefs?.card_deck || DEFAULT_CARD_DECK;

  // Map { action_key → url } for the currently selected deck. Null means
  // /available-decks is still loading; an empty object means the response
  // loaded but did not include URLs for the selected deck.
  const deckCards = useMemo(() => {
    if (!availableDecks) return null;
    const entry = availableDecks?.decks?.find((d) => d?.name === cardDeck);
    return entry?.cards || {};
  }, [availableDecks, cardDeck]);

  // BOU-2699: default-on. `undefined` is "not loaded yet", not "opted out" —
  // reading it as off would suppress the travel cinematic on every first paint.
  const cinematicTravel = prefs?.cinematic_travel !== false;

  const value = {
    prefs,
    cardDeck,
    cinematicTravel,
    availableDecks,
    deckCards,
    refresh,
    applyPreferences,
  };

  return (
    <PlayerPreferencesContext.Provider value={value}>
      {children}
    </PlayerPreferencesContext.Provider>
  );
}

export function usePlayerPreferences() {
  const ctx = useContext(PlayerPreferencesContext);
  if (!ctx) {
    return {
      prefs: null,
      cardDeck: DEFAULT_CARD_DECK,
      cinematicTravel: true,
      availableDecks: null,
      deckCards: null,
      refresh: async () => null,
      applyPreferences: () => {},
    };
  }
  return ctx;
}

export default PlayerPreferencesContext;
